import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './PaginatedStoreGrid.css';

// Use a relative path so Nginx proxies it to your Express server over HTTPS
import { API_BASE, productImageUrl } from '../apiConfig';

const PaginatedStoreGrid = ({ storeName, itemType = 'product', categorySlug = null, onClearCategory }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageDebug, setImageDebug] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const navigate = useNavigate();
  const { city } = useParams();

  // Helper function to get the best image URL from a product
  const getProductImageUrl = (product) => {
    // New multiple images system
    if (product.images && product.images.length > 0) {
      // Find primary image first
      const primaryImage = product.images.find(img => img.is_primary);
      if (primaryImage) {
        return primaryImage.image_url;
      }
      
      // If no primary image, use first image
      return product.images[0].image_url;
    }
    
    // Fallback to legacy single image_url
    if (product.image_url) {
      return product.image_url;
    }
    
    // No image available
    return null;
  };

  // Helper function to process image URLs
  const processImageUrl = productImageUrl;

  // Reset when item type, items-per-page, or category filter changes
  useEffect(() => {
    setProducts([]);
    setCurrentPage(1);
    fetchProducts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, itemType, itemsPerPage, categorySlug]);

  const fetchProducts = async (page = 1, append = false) => {
    console.log(`PaginatedStoreGrid: Fetching ${itemType}s for`, storeName);

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      console.log(`Fetching ${itemType}s for store "${storeName}" - page ${page}, limit ${itemsPerPage}`);
      
      // Use the dedicated paginated endpoint with item_type and optional category filter
      const catParam = categorySlug ? `&category=${encodeURIComponent(categorySlug)}` : '';
      const response = await fetch(`${API_BASE}/stores/${storeName}/products?page=${page}&limit=${itemsPerPage}&item_type=${itemType}${catParam}`);

      if (!response.ok) {
        throw new Error(`Error loading ${itemType}s (Status: ${response.status})`);
      }
      
      const data = await response.json();
      console.log(`Received page ${data.pagination.page} of ${data.pagination.totalPages} (${data.products.length} ${itemType}s)`);
      
      // Store image debug info
      const debugInfo = {};
      data.products.forEach(p => {
        const bestImageUrl = getProductImageUrl(p);
        debugInfo[p.id] = {
          imagesCount: p.images ? p.images.length : 0,
          hasLegacyImage: !!p.image_url,
          original: bestImageUrl,
          processed: processImageUrl(bestImageUrl),
          loaded: false,
          error: false
        };
      });
      
      // Update state
      if (append) {
        setProducts(prev => [...prev, ...data.products]);
        setImageDebug(prev => ({ ...prev, ...debugInfo }));
      } else {
        setProducts(data.products);
        setImageDebug(debugInfo);
      }
      
      setTotalPages(data.pagination.totalPages);
      setTotalProducts(data.pagination.total);
      setCurrentPage(data.pagination.page);
      
    } catch (err) {
      console.error(`Error fetching ${itemType}s:`, err);
      setError(`Error loading ${itemType}s: ${err.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/${city}/${storeName}/product/${productId}`);
  };

  // Handle image load success
  const handleImageLoad = (productId) => {
    setImageDebug(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        loaded: true
      }
    }));
  };

  // Handle image load error
  const handleImageError = (productId, originalUrl, event) => {
    console.error(`Failed to load image for ${itemType} ID: ${productId}, URL: ${event.target.src}`);
    
    // Store error information
    setImageDebug(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        error: true,
        errorUrl: event.target.src
      }
    }));
    
    // Stop failed-image retries and leave the card's existing placeholder visible.
    event.target.onerror = null;
    event.target.style.display = 'none';

  };

  // Load more products
  const handleLoadMore = () => {
    if (currentPage < totalPages && !loadingMore) {
      fetchProducts(currentPage + 1, true);
    }
  };

  // Change items per page
  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    // Reset to page 1 when changing items per page
    setCurrentPage(1);
  };

  // Render placeholder for products without images
  const renderImageOrPlaceholder = (product) => {
    const imageUrl = getProductImageUrl(product);
    const processedUrl = processImageUrl(imageUrl);
    
    if (!processedUrl || (itemType === 'product' && imageDebug[product.id]?.error)) {
      // No image available - show placeholder
      return (
        <div className={itemType === 'product' ? 'store-product-placeholder' : 'img-fluid w-100 h-100 d-flex align-items-center justify-content-center bg-light'}>
          <div className="text-center text-muted">
            <i className="fas fa-image fa-2x mb-2"></i>
            <div style={{ fontSize: '0.8rem' }}>No Image</div>
          </div>
        </div>
      );
    }
    
    return (
      <img
        src={processedUrl}
        alt={product.name}
        className={itemType === 'product' ? 'store-product-image' : 'img-fluid w-100 h-100 object-fit-cover'}
        onLoad={() => handleImageLoad(product.id)}
        onError={(e) => handleImageError(product.id, imageUrl, e)}
      />
    );
  };

  // Get proper item type label for display
  const getItemTypeLabel = () => {
    switch(itemType) {
      case 'product': return 'Products';
      case 'service': return 'Services';
      case 'experience': return 'Experiences';
      default: return 'Products';
    }
  };

  if (loading && currentPage === 1) {
    return <div className="container text-center py-5">Loading {getItemTypeLabel()}...</div>;
  }
  if (error) {
    return <div className="container text-center py-5 text-danger">{error}</div>;
  }

  return (
    <div className={`container px-4 ${itemType === 'product' ? 'store-product-catalog' : 'store-media-catalog'}`}>
      {/* Items per page selector */}
      <div className="d-flex justify-content-between align-items-center mb-3 store-catalog-controls">
        <div className="text-muted small">
          Showing {products.length} of {totalProducts} {totalProducts === 1 ? itemType : `${itemType}s`}
        </div>
        
        <div className="d-flex align-items-center">
          <span className="me-2 text-muted small">Items per page:</span>
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto' }}
            value={itemsPerPage} 
            onChange={handleItemsPerPageChange}
          >
            <option value="12">12</option>
            <option value="24">24</option>
            <option value="48">48</option>
          </select>
        </div>
      </div>
      
      {/* Retail cards are limited to products; services and experiences keep their layout. */}
      {itemType === 'product' ? (
        <div className="store-product-grid">
          {products.map(product => (
            <Link
              key={product.id}
              to={`/${city}/${storeName}/product/${product.id}`}
              className="store-product-card"
            >
              <div className="store-product-image-area">
                {renderImageOrPlaceholder(product)}
                {product.images && product.images.length > 1 && (
                  <span className="store-product-image-count" aria-label={`${product.images.length} images`}>
                    <span aria-hidden="true">▧</span> {product.images.length}
                  </span>
                )}
              </div>
              <div className="store-product-info">
                <h3 className="store-product-name">{product.name}</h3>
                <p className="store-product-price">
                  {product.price_type === 'from' && <span className="store-product-price-prefix">From </span>}
                  ${Number(product.price).toFixed(2)} <span className="store-product-currency">CAD</span>
                </p>
                {product.availability === 'out_of_stock' && <p className="store-product-availability">Sold out online</p>}
                {product.availability === 'preorder' && <p className="store-product-availability">Preorder</p>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="row g-0">
          {products.map((product) => (
            <div
              key={product.id}
              className="col-4 p-1"
              onClick={() => handleProductClick(product.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="position-relative instagram-card-visible">
                <div className="aspect-ratio-container">
                  {renderImageOrPlaceholder(product)}
                  <div className="visible-overlay">
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-price">{product.price_type === 'from' ? 'From ' : ''}${Number(product.price).toFixed(2)}</p>
                      {product.availability === 'out_of_stock' && <p className="text-muted">Sold out online</p>}
                      {product.availability === 'preorder' && <p className="text-muted">Preorder</p>}
                      {/* Show image count indicator for products with multiple images */}
                      {product.images && product.images.length > 1 && (
                        <div className="position-absolute top-0 end-0 m-2">
                          <span className="badge bg-dark bg-opacity-75">
                            <i className="fas fa-images"></i> {product.images.length}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No products message */}
      {products.length === 0 && !loading && !error && (
        <div className="text-center py-5">
          {categorySlug ? (
            <>
              <p className="mb-2">
                No {itemType === 'product' ? 'products' : itemType === 'service' ? 'services' : 'experiences'} match this category.
              </p>
              {onClearCategory && (
                <button type="button" className="btn btn-link" onClick={onClearCategory}>
                  Clear filter
                </button>
              )}
            </>
          ) : (
            <p className="mb-0">No {itemType === 'product' ? 'products' : itemType === 'service' ? 'services' : 'experiences'} available at this time.</p>
          )}
        </div>
      )}
      
      {/* Load more button or pagination */}
      {totalPages > 1 && currentPage < totalPages && (
        <div className="text-center mt-4 mb-5">
          <button 
            className="btn btn-outline-primary"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading more...
              </>
            ) : (
              <>Load More {getItemTypeLabel()}</>
            )}
          </button>
        </div>
      )}
      
      {/* Page indicator */}
      {totalPages > 1 && (
        <div className="text-center text-muted small mb-4">
          Page {currentPage} of {totalPages}
        </div>
      )}

      {/* Inline styles */}
      <style>{`
        .store-media-catalog .instagram-card-visible {
          position: relative;
          overflow: hidden;
        }

        .store-media-catalog .aspect-ratio-container {
          position: relative;
          width: 100%;
          padding-bottom: 100%; /* 1:1 Aspect Ratio */
          overflow: hidden;
          background-color: #f8f9fa;
        }

        .store-media-catalog .aspect-ratio-container img,
        .store-media-catalog .aspect-ratio-container > div {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .store-media-catalog .visible-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 70%);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .store-media-catalog .instagram-card-visible:hover .visible-overlay {
          background: radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 70%);
        }

        .store-media-catalog .product-info {
          text-align: center;
          color: white;
          padding: 10px;
        }

        .store-media-catalog .product-name {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0;
          line-height: 1.2;
          text-shadow: 0 2px 4px rgba(0,0,0,0.8);
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .store-media-catalog .product-price {
          font-size: 0.85rem;
          font-weight: 500;
          margin: 4px 0 0 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.8);
        }

        @media (max-width: 768px) {
          .store-media-catalog .product-name {
            font-size: 0.8rem;
          }
          .store-media-catalog .product-price {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PaginatedStoreGrid;